import type { Instant, LocalDate, WrongNote } from './index';

import { addCalendarDays, calendarDayDifference, localDateAt } from './local-date';

export const WRONG_NOTE_REVIEW_INTERVAL_DAYS = [14, 28, 56] as const;

export type WrongNoteReviewSchedule = {
  anchorDate: LocalDate;
  dueDate: LocalDate;
  intervalDays: number;
  state: 'scheduled' | 'due' | 'overdue' | 'relearning' | 'mastered';
  overdueDays: number;
};

function intervalForReviewCount(reviewCount: number): number {
  if (!Number.isInteger(reviewCount) || reviewCount < 0) {
    throw new Error('Wrong note reviewCount must be a non-negative integer');
  }
  return (
    WRONG_NOTE_REVIEW_INTERVAL_DAYS[Math.min(reviewCount, 2)] ?? WRONG_NOTE_REVIEW_INTERVAL_DAYS[2]
  );
}

/**
 * Derives a local-calendar review schedule. It never advances a missed review:
 * the next interval begins only after the learner records an actual review.
 */
export function scheduleWrongNoteReview(
  note: Pick<
    WrongNote,
    'createdAt' | 'lastReviewedAt' | 'masteredAt' | 'relearningAt' | 'reviewCount' | 'lastRecall'
  >,
  today: LocalDate,
  timezone: string,
): WrongNoteReviewSchedule {
  if (note.masteredAt) {
    return {
      anchorDate: localDateAt(note.masteredAt, timezone),
      dueDate: localDateAt(note.masteredAt, timezone),
      intervalDays: 0,
      state: 'mastered',
      overdueDays: 0,
    };
  }

  if (note.relearningAt) {
    const relearningDate = localDateAt(note.relearningAt, timezone);
    return {
      anchorDate: relearningDate,
      dueDate: relearningDate,
      intervalDays: 0,
      state: 'relearning',
      overdueDays: Math.max(0, calendarDayDifference(relearningDate, today)),
    };
  }

  const anchorDate = localDateAt(note.lastReviewedAt ?? note.createdAt, timezone);
  const intervalDays =
    note.lastRecall === 'forgotten'
      ? 3
      : note.lastRecall === 'uncertain'
        ? 7
        : intervalForReviewCount(note.reviewCount);
  const dueDate = addCalendarDays(anchorDate, intervalDays);
  const difference = calendarDayDifference(dueDate, today);
  return {
    anchorDate,
    dueDate,
    intervalDays,
    state: difference < 0 ? 'scheduled' : difference === 0 ? 'due' : 'overdue',
    overdueDays: Math.max(0, difference),
  };
}

export function isWrongNoteReadyForReview(
  schedule: WrongNoteReviewSchedule,
): schedule is WrongNoteReviewSchedule & { state: 'due' | 'overdue' | 'relearning' } {
  return (
    schedule.state === 'due' || schedule.state === 'overdue' || schedule.state === 'relearning'
  );
}

export function beginWrongNoteRelearning(note: WrongNote, at: Instant): WrongNote {
  const relearning: WrongNote = {
    ...note,
    reviewCount: 0,
    relearningAt: at,
    updatedAt: at,
    revision: note.revision + 1,
  };
  delete relearning.masteredAt;
  delete relearning.lastReviewedAt;
  return relearning;
}
